import { useEffect, useState, type FormEvent } from "react";
import { cleanName } from "../../lib/names";

// One category tree (expense or income) as the settings screen edits it: a local copy of the
// names and their subcategories, compared against the saved one to know what changed.

/** An editable copy of each category's subcategories, one entry per category even if it has none. */
const copyTree = (categories: string[], subcategoriesByCategory: Record<string, string[]>) =>
  Object.fromEntries(
    categories.map((category) => [category, [...(subcategoriesByCategory[category] || [])]]),
  ) as Record<string, string[]>;

export function useCategoryEditor(
  categories: string[],
  subcategoriesByCategory: Record<string, string[]>,
) {
  const [list, setList] = useState(() => [...categories]);
  const [map, setMap] = useState(() => copyTree(categories, subcategoriesByCategory));
  const [newCategory, setNewCategory] = useState("");
  const [newSubcategory, setNewSubcategory] = useState<Record<string, string>>(
    {},
  );
  useEffect(() => {
    setList([...categories]);
    setMap(copyTree(categories, subcategoriesByCategory));
    // Re-sync only when the server's own copy changes, not on every local edit.
  }, [categories, subcategoriesByCategory]);
  const dirty =
    JSON.stringify(list) !== JSON.stringify(categories) ||
    JSON.stringify(map) !== JSON.stringify(subcategoriesByCategory);
  function renameCategory(oldName: string, nextValue: string) {
    if (oldName === "Uncategorized") return;
    const next = cleanName(nextValue);
    if (!next || next === oldName) return;
    if (list.some((c) => c.toLowerCase() === next.toLowerCase())) return;
    setList((current) => current.map((c) => (c === oldName ? next : c)).sort());
    setMap((current) => {
      const copy = { ...current, [next]: current[oldName] || [] };
      delete copy[oldName];
      return copy;
    });
  }
  function addCategory(e: FormEvent) {
    e.preventDefault();
    const next = cleanName(newCategory);
    if (!next || list.some((c) => c.toLowerCase() === next.toLowerCase()))
      return;
    setList((current) => [...current, next].sort());
    setMap((current) => ({ ...current, [next]: [] }));
    setNewCategory("");
  }
  function removeCategory(category: string) {
    if (category === "Uncategorized") return;
    setList((current) => current.filter((c) => c !== category));
    setMap((current) => {
      const copy = { ...current };
      delete copy[category];
      return copy;
    });
  }
  function addSubcategory(category: string) {
    if (category === "Uncategorized") return;
    const next = cleanName(newSubcategory[category] || "");
    if (!next) return;
    setMap((current) => {
      const children = current[category] || [];
      if (children.some((s) => s.toLowerCase() === next.toLowerCase()))
        return current;
      return { ...current, [category]: [...children, next].sort() };
    });
    setNewSubcategory((current) => ({ ...current, [category]: "" }));
  }
  function renameSubcategory(
    category: string,
    oldName: string,
    nextValue: string,
  ) {
    const next = cleanName(nextValue);
    if (!next || next === oldName) return;
    setMap((current) => {
      const children = current[category] || [];
      if (
        children.some(
          (s) => s !== oldName && s.toLowerCase() === next.toLowerCase(),
        )
      )
        return current;
      return {
        ...current,
        [category]: children.map((s) => (s === oldName ? next : s)).sort(),
      };
    });
  }
  function removeSubcategory(category: string, subcategory: string) {
    setMap((current) => ({
      ...current,
      [category]: (current[category] || []).filter((s) => s !== subcategory),
    }));
  }
  return {
    list,
    map,
    dirty,
    newCategory,
    setNewCategory,
    newSubcategory,
    setNewSubcategory,
    addCategory,
    removeCategory,
    renameCategory,
    addSubcategory,
    removeSubcategory,
    renameSubcategory,
  };
}
